import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { Link, withRouter, locationShape } from 'react-router';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classnames from 'classnames';
import { defineMessages, FormattedMessage, injectIntl, intlShape } from 'react-intl';
import IconLabel from '../util/IconLabel';
import styles from './MainMenu.css';
import * as analyticsActions from '../../actions/analytics';

import CollectionLinks from '../common/CollectionLinks';
import AccountJoin from '../messages/AccountJoin';
import AccountLogin from '../messages/AccountLogin';
import MenuDiscover from '../messages/MenuDiscover';
import MenuBrowse from '../messages/MenuBrowse';
import MenuMood from '../messages/MenuMood';
import MenuRecentlyPlayed from '../messages/MenuRecentlyPlayed';
import { selectLocale } from '../../selectors/client';
import GetPremiumButton from '../common/GetPremiumButton';

import { LANGUAGE_SUPPORT_LINKS, CONSTANT_MESSAGES } from '../../constants';
import { splitLocale, getWebsiteUrlInLocale } from '../../lib/locale-utils';
import MenuConcertsAndCourses from '../messages/MenuConcertsAndCourses';
import { messages as footerMessages } from '../messages/Footer';
import OutsideClickHandler from 'react-outside-click-handler';
import { commonMessages } from '../messages/Common';

const messages = defineMessages({
  aboutLink: {
    id: 'menu.about',
    defaultMessage: 'About IDAGIO',
  },
  supportLink: {
    id: 'menu.help',
    defaultMessage: 'Help & Contact',
  },
  giftCard: {
    id: 'menu.gift-card',
    defaultMessage: 'Gift Card',
  },
});

class MainMenu extends PureComponent {
  static propTypes = {
    isAuthenticated: PropTypes.bool.isRequired,
    isMac: PropTypes.bool.isRequired,
    isWindows: PropTypes.bool.isRequired,
    logout: PropTypes.func.isRequired,
    className: PropTypes.string,
    track: PropTypes.func.isRequired,
    locale: PropTypes.string.isRequired,
    intl: intlShape,
    location: locationShape.isRequired,
    showSubscribeButton: PropTypes.bool.isRequired,
  };

  state = {
    menuIsOpen: false,
  };

  renderUpperUserMenu() {
    if (this.props.showSubscribeButton) {
      return (
        <li>
          <GetPremiumButton
            className={styles.link}
            onClick={this.closeMenu}
            iconName="premium-filled"
            iconSize="medium"
          />
        </li>
      );
    }
    return null;
  }

  renderUpperStaticMenu() {
    return (
      <React.Fragment>
        <li key="discover">
          <Link
            to="/discover"
            className={styles.linkSmallView}
            onlyActiveOnIndex={false}
            onClick={this.closeMenu}
            data-test="main-menu-discover-link"
          >
            <MenuDiscover />
          </Link>
        </li>
        <li key="browse">
          <Link
            to="/browse"
            className={styles.linkSmallView}
            onlyActiveOnIndex={false}
            onClick={this.closeMenu}
            data-test="main-menu-browse-link"
          >
            <MenuBrowse />
          </Link>
        </li>
        <li key="moods">
          <Link
            to="/moods"
            className={styles.linkSmallView}
            onlyActiveOnIndex={false}
            onClick={this.closeMenu}
            data-test="main-menu-moods-link"
          >
            <MenuMood />
          </Link>
        </li>
        <li key="live">
          <Link
            to="/live"
            className={styles.linkSmallView}
            onlyActiveOnIndex={false}
            onClick={this.closeMenu}
            data-test="main-menu-live-link"
          >
            <MenuConcertsAndCourses />
          </Link>
        </li>
        <li key="recently-played">
          <Link
            to="/recently-played"
            className={styles.linkSmallView}
            onlyActiveOnIndex={false}
            onClick={this.closeMenu}
            data-test="main-menu-recently-played-link"
          >
            <MenuRecentlyPlayed />
          </Link>
        </li>
      </React.Fragment>
    );
  }

  renderLowerStaticMenu() {
    const { intl, locale, isMac, isWindows } = this.props;

    const giftCardText = intl.formatMessage(messages.giftCard);
    const ariaGiftCardLink = intl.formatMessage(CONSTANT_MESSAGES.externalLink, {
      linkText: giftCardText,
    });

    const termsText = intl.formatMessage(footerMessages.termsLink);
    const ariaTermsLink = intl.formatMessage(CONSTANT_MESSAGES.externalLink, {
      linkText: termsText,
    });

    const privacyText = intl.formatMessage(footerMessages.privacyLink);
    const ariaPrivacyLink = intl.formatMessage(CONSTANT_MESSAGES.externalLink, {
      linkText: privacyText,
    });

    const supportText = intl.formatMessage(messages.supportLink);
    const ariaSupportLink = intl.formatMessage(CONSTANT_MESSAGES.externalLink, {
      linkText: supportText,
    });

    const aboutText = intl.formatMessage(messages.aboutLink);
    const ariaAboutLink = intl.formatMessage(CONSTANT_MESSAGES.externalLink, {
      linkText: aboutText,
    });

    return (
      <React.Fragment>
        <li key="help">
          <a
            href={LANGUAGE_SUPPORT_LINKS[splitLocale(locale).language]}
            className={styles.link}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ariaSupportLink}
            onClick={this.closeMenu}
            data-test="main-menu-help-link"
          >
            {supportText}
          </a>
        </li>
        {!__ELECTRON__ && (isMac || isWindows) && (
          <li>
            <Link
              to="/install"
              className={styles.link}
              onClick={this.trackDownloadDesktop}
              onlyActiveOnIndex={false}
            >
              <FormattedMessage id="menu.download-app" defaultMessage="Download App" />
            </Link>
          </li>
        )}
        <li>
          <a
            className={styles.link}
            href={getWebsiteUrlInLocale('/promotions/gift-card', locale)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ariaGiftCardLink}
            onClick={this.closeMenu}
            data-test="main-menu-gift-card-link"
          >
            {giftCardText}
          </a>
        </li>
        {!__ELECTRON__ && (
          <li key="about">
            <a
              href={getWebsiteUrlInLocale('/about', locale)}
              className={styles.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={ariaAboutLink}
              data-test="main-menu-about-link"
              onClick={this.closeMenu}
            >
              {aboutText}
            </a>
          </li>
        )}
        <li>
          <a
            className={styles.link}
            href={getWebsiteUrlInLocale('/terms', locale)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ariaTermsLink}
            onClick={this.closeMenu}
            data-test="main-menu-terms-link"
          >
            {termsText}
          </a>
        </li>
        <li>
          <a
            className={styles.link}
            href={getWebsiteUrlInLocale('/privacy', locale)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ariaPrivacyLink}
            onClick={this.closeMenu}
            data-test="main-menu-privacy-link"
          >
            {privacyText}
          </a>
        </li>
      </React.Fragment>
    );
  }

  renderLowerUserMenu() {
    const { isAuthenticated, location } = this.props;
    if (isAuthenticated) {
      return (
        <li>
          <Link
            to="/account"
            className={styles.link}
            onlyActiveOnIndex={false}
            onClick={this.closeMenu}
          >
            <FormattedMessage id="menu.settings" defaultMessage="Settings" />
          </Link>
        </li>
      );
    }

    const to = `/${location.pathname}${location.search}`;

    return (
      <React.Fragment>
        <li>
          <Link
            to={`/login?to=${encodeURIComponent(to)}`}
            className={styles.linkSmallMediumView}
            onlyActiveOnIndex={false}
            data-test="main-menu-login-link"
          >
            <AccountLogin />
          </Link>
        </li>
        <li>
          <Link
            to={`/join?to=${encodeURIComponent(to)}`}
            className={styles.linkSmallMediumView}
            onlyActiveOnIndex={false}
            data-test="main-menu-join-link"
          >
            <AccountJoin />
          </Link>
        </li>
      </React.Fragment>
    );
  }

  renderLogout() {
    const { isAuthenticated } = this.props;
    if (isAuthenticated) {
      return (
        <li>
          <button className={styles.link} onClick={this.props.logout}>
            <FormattedMessage id="account.logout" defaultMessage="Log Out" />
          </button>
        </li>
      );
    }

    return null;
  }

  render() {
    const mainMenuClassNames = classnames(styles.component, this.props.className, {
      'is-expanded': this.state.menuIsOpen,
    });
    return (
      <div className={mainMenuClassNames}>
        <OutsideClickHandler onOutsideClick={this.handleClickOutside}>
          <button
            className={styles.toggle}
            onClick={this.toggleMenu}
            aria-label={
              this.state.menuIsOpen
                ? this.props.intl.formatMessage(commonMessages.closeMenu)
                : this.props.intl.formatMessage(commonMessages.openMenu)
            }
            data-test="main-menu-toggle"
          >
            <IconLabel name="menu" className={styles.icon} />
          </button>
          <div className={styles.list}>
            <ul>
              {this.renderUpperUserMenu()}
              {this.renderUpperStaticMenu()}
              {this.props.isAuthenticated && (
                <CollectionLinks linkClassName={styles.linkSmallView} onClick={this.closeMenu} />
              )}
              {this.renderLowerUserMenu()}
              {this.renderLowerStaticMenu()}
              {this.renderLogout()}
            </ul>
          </div>
        </OutsideClickHandler>
      </div>
    );
  }

  trackDownloadDesktop = () => {
    this.props.track('Clicked Download Desktop App', { page: 'MainMenu' });
  };

  toggleMenu = () => this.setState(prevState => ({ menuIsOpen: !prevState.menuIsOpen }));

  closeMenu = () => this.setState({ menuIsOpen: false });

  handleClickOutside = () => this.closeMenu();
}

function mapStateToProps(state) {
  return {
    locale: selectLocale(state),
  };
}

export default compose(
  injectIntl,
  connect(mapStateToProps, { track: analyticsActions.track }),
  withRouter
)(MainMenu);
